import { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { apiFetch, getToken, DPP_BASE_URL } from '../dppApi';
import ProductCard from './ProductCard';

// Discover — unsold pieces from participating brands.
//
// This tab has NO input of its own. Filtering is Ask Bloom's job: the assistant
// classifies a browsing request, derives a filter from the user's own words, and
// hands it here through the `filter` prop. Everything below is display.
//
// The chips are the exception, and they are output rather than input — they show
// what the assistant understood, and let it be corrected without re-typing a
// sentence. That matters most for dictated queries, where a mishearing is
// otherwise invisible until the results look wrong.
//
// Cards reuse the collection card wholesale (.collection-card and friends) so a
// piece the user does not own looks like one they do — same frame, same brand
// strip, same image well. Only the body differs: an unowned piece has a price
// and a spec line where an owned one has its documents.

const CHIP_ORDER = ['brand', 'category', 'metal', 'purity', 'stones', 'weight_g', 'price'];

const money = (amount, currency) => {
    if (amount === null || amount === undefined) return null;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        // An unknown or malformed ISO code must not blank the price.
        return `${currency || ''} ${Math.round(amount).toLocaleString()}`.trim();
    }
};

// A filter field -> the words shown on its chip. Returns null for fields that are
// unset, so the caller can simply filter out the blanks.
const chipLabel = (key, value) => {
    if (value === null || value === undefined || value === '') return null;
    if (key === 'stones') return value.length ? value.map(cap).join(', ') : null;
    if (key === 'weight_g') return range(value, v => `${v}g`);
    if (key === 'price') {
        const { currency, ...bounds } = value;
        return range(bounds, v => money(v, currency) || v);
    }
    return cap(value);
};

const cap = (s) => (typeof s === 'string' && s ? s[0].toUpperCase() + s.slice(1) : s);

const range = (v, fmt) => {
    if (!v || typeof v !== 'object') return null;
    const { min, max } = v;
    if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
    if (max != null) return `Under ${fmt(max)}`;
    if (min != null) return `Over ${fmt(min)}`;
    return null;
};

// The brand dashboard's chevron, same 11px SVG and the same 90° rotation, so a
// collapsed group looks identical wherever it appears.
const Chevron = ({ open }) => (
    <span className={`inquiry-group-chevron${open ? ' is-open' : ''}`} aria-hidden="true">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
        </svg>
    </span>
);

// What the user has waved away in this row, under the cards and above the arrows.
//
// Always a group, never a bare line — a single dismissal collapses the same way
// as eight, so the control does not change shape as the count grows. Collapsed
// it is one line whatever the number, which is the point: a list of rejections
// must never grow taller than the recommendations it sits beneath.
const DismissedGroup = ({ entries, onUndo, onRestoreAll }) => {
    const [open, setOpen] = useState(false);
    if (!entries.length) return null;
    return (
        <div className="discover-dismissed">
            <button type="button" className="discover-dismissed-summary"
                    onClick={() => setOpen(o => !o)} aria-expanded={open}>
                <Chevron open={open} />
                Dismissed
                <span className="inquiry-group-count">{entries.length}</span>
            </button>
            {open && (
                <div className="discover-dismissed-list">
                    {entries.map(e => (
                        <div className="discover-dismissed-item" key={e.card.product_id}>
                            <span className="discover-dismissed-name">{e.card.name}</span>
                            <button type="button" className="bsp-editlink"
                                    onClick={() => onUndo(e)}>Undo</button>
                        </div>
                    ))}
                    {/* Only past one — with a single item it would repeat the Undo
                        sitting beside it. */}
                    {entries.length > 1 && (
                        <div className="discover-dismissed-item">
                            <button type="button" className="bsp-editlink"
                                    onClick={onRestoreAll}>Restore all</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// One default row: a reason, then a whole number of cards, then its arrows.
//
// Paged rather than scrolled. A scroller has to cut a card off at the edge to
// show there is more, and a half-card reads as a rendering fault rather than an
// affordance — so the row measures itself, shows only as many cards as fit
// COMPLETELY, and moves a page at a time.
//
// Card width is MEASURED, never assumed. It is set in CSS and changes at a
// media query, so hardcoding it here means two sources of truth that disagree:
// the query switches on VIEWPORT width while this row can only see its own
// CONTAINER, and between roughly 560 and 620px those differ enough to fit two
// 280px cards into a 540px row. Reading the rendered width cannot drift.
const CARD_W_FALLBACK = 280;   // only for the frame before anything has rendered
const GAP = 20;                // matches .discover-cards { gap }

const PagedRow = ({ reason, cards, renderCard, dismissed, onUndo, onRestoreAll }) => {
    const ref = useRef(null);
    const [perPage, setPerPage] = useState(cards.length);
    const [page, setPage] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const measure = () => {
            const w = el.clientWidth;
            const first = el.firstElementChild;
            const card = first ? first.getBoundingClientRect().width : CARD_W_FALLBACK;
            if (!w || !card) return;
            // n cards have n-1 gaps, so the arithmetic is (w + GAP) / (card + GAP)
            // rather than w / (card + GAP).
            setPerPage(Math.max(1, Math.floor((w + GAP) / (card + GAP))));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // A narrower window means fewer per page, which can strand the view past the
    // end — so the page is clamped on read rather than stored corrected.
    const pageCount = Math.max(1, Math.ceil(cards.length / perPage));
    const current = Math.min(page, pageCount - 1);
    const shown = cards.slice(current * perPage, current * perPage + perPage);

    return (
        <div className="discover-row">
            <div className="discover-reason">{reason}</div>
            <div className="discover-cards" ref={ref}>{shown.map(renderCard)}</div>
            <DismissedGroup entries={dismissed} onUndo={onUndo} onRestoreAll={onRestoreAll} />
            {pageCount > 1 && (
                <nav className="pager" aria-label={`${reason} pagination`}>
                    <button type="button" className="pager-btn" aria-label="Previous"
                            onClick={() => setPage(current - 1)} disabled={current === 0}>&lsaquo;</button>
                    <button type="button" className="pager-btn" aria-label="Next"
                            onClick={() => setPage(current + 1)}
                            disabled={current >= pageCount - 1}>&rsaquo;</button>
                </nav>
            )}
        </div>
    );
};

const UserDiscover = ({ filter, onFilterChange }) => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [omitted, setOmitted] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // The filter object identity changes on every assistant turn, so the fetch is
    // keyed on its CONTENT — otherwise a reply that changed nothing would still
    // refetch and re-log a full set of impressions.
    const filterKey = JSON.stringify(filter || {});

    // The token is read HERE rather than held in a variable the effect depends on.
    // apiFetch writes a refreshed token to localStorage on every authenticated
    // call (the sliding session), so a `token` dependency changes the moment the
    // fetch returns and re-runs the effect — three round trips on every open,
    // three "Loading…" flashes, and it only stops when the server happens to
    // mint an identical string.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        apiFetch('/api/user/discover', {
            method: 'POST', token: getToken('user'), body: { filter: JSON.parse(filterKey) },
        })
            .then(d => {
                if (cancelled) return;
                setRows(d.rows || []);
                setTotal(d.total || 0);
                setOmitted(d.omitted_no_weight || 0);
            })
            .catch(e => { if (!cancelled) setError(e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [filterKey]);

    // Dismissals held per row, newest first, WITH the card itself — Undo puts the
    // piece back where it was rather than refetching, which would reshuffle the
    // whole row (candidates come back in random order) and lose the user's place.
    const [dismissed, setDismissed] = useState({});

    const rowKey = (reason) => reason || '';

    // Drop the card from view at once. The server will not offer it again, but
    // that only takes effect on the next load — leaving it on screen until then
    // makes the × look broken.
    const dismiss = useCallback((reason, card) => {
        const key = rowKey(reason);
        setRows(rs => rs.map(r => rowKey(r.reason) !== key ? r : {
            ...r,
            cards: r.cards.filter(c => c.product_id !== card.product_id),
        }));
        setDismissed(d => ({
            ...d,
            // Remember the index so the card returns to its own place in the row.
            [key]: [{ card, index: 0 }, ...(d[key] || [])],
        }));
        setTotal(t => Math.max(0, t - 1));
    }, []);

    const restore = useCallback((reason, entries) => {
        const key = rowKey(reason);
        const ids = entries.map(e => e.card.product_id);
        setRows(rs => rs.map(r => rowKey(r.reason) !== key ? r : {
            ...r,
            cards: [...entries.map(e => e.card), ...r.cards],
        }));
        setDismissed(d => ({ ...d, [key]: (d[key] || []).filter(e => !ids.includes(e.card.product_id)) }));
        setTotal(t => t + entries.length);
        apiFetch('/api/user/discover/event', {
            method: 'POST', token: getToken('user'),
            body: { action: 'restored', product_ids: ids },
        }).catch(() => {});
    }, []);

    // 'opened' and 'dismissed' only — 'shown' is written server-side when the
    // cards are served, so the client cannot inflate impressions.
    const logEvent = useCallback((productId, action) => {
        apiFetch('/api/user/discover/event', {
            method: 'POST', token: getToken('user'),
            body: { product_id: productId, action, filter: JSON.parse(filterKey) },
        }).catch(() => {});   // analytics must never interrupt navigation
    }, [filterKey]);

    // Dropping the last MEANINGFUL term must hand back {}, not the model's
    // leftover empty keys — the server treats those as no search either, but
    // sending them would refetch for nothing and briefly show the wrong thing.
    const dropChip = (key) => {
        if (!onFilterChange) return;
        const next = { ...(filter || {}) };
        delete next[key];
        const stillFiltered = CHIP_ORDER.some(k => chipLabel(k, next[k]));
        onFilterChange(stillFiltered ? next : {});
    };

    const chips = CHIP_ORDER
        .map(key => ({ key, label: chipLabel(key, (filter || {})[key]) }))
        .filter(c => c.label);

    // The same card the Collection tab renders. What an unowned piece adds below
    // the title is a price and a spec line, where an owned one has its documents.
    const renderCard = (reason) => (c) => (
        <ProductCard
            p={c}
            key={c.product_id}
            overlay={
                <button
                    className="discover-dismiss"
                    onClick={() => { logEvent(c.product_id, 'dismissed'); dismiss(reason, c); }}
                    title="Not for me"
                    aria-label={`Dismiss ${c.name}`}
                >
                    ×
                </button>
            }
        >
            {/* One meta line, the same class the brand dashboard puts metal and
                purity in — price first because it is the thing an unowned piece
                is asked about. */}
            {(c.price != null || c.spec) && (
                <div className="collection-card-meta">
                    {[money(c.price, c.currency), c.spec].filter(Boolean).join(' · ')}
                </div>
            )}

            {/* Actions and QR share a footer row, as on the Collection card and the
                brand dashboard, so the code stays anchored beside the button
                rather than floating in the card. */}
            <div className="collection-card-footer">
                <div className="collection-card-actions">
                    <a
                        className="collection-action"
                        href={`${DPP_BASE_URL}/jewellery/${c.qr_code_id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => logEvent(c.product_id, 'opened')}
                    >
                        View passport
                    </a>
                </div>
                <QRCodeSVG
                    className="collection-card-qr"
                    value={`${DPP_BASE_URL}/jewellery/${c.qr_code_id}`}
                    size={64}
                    marginSize={2}
                />
            </div>
        </ProductCard>
    );

    if (loading) return <p className="collection-status collection-status--flush section-empty">Loading…</p>;
    if (error) return <p className="dpp-error">{error}</p>;

    const hasFilter = chips.length > 0;

    return (
        <>
            {hasFilter && (
                <div className="discover-chips">
                    {chips.map(c => (
                        <button key={c.key} className="collection-action" onClick={() => dropChip(c.key)}
                                title={`Remove ${c.label}`}>
                            {c.label}<span className="dpp-muted" aria-hidden="true">&nbsp;×</span>
                        </button>
                    ))}
                </div>
            )}

            {rows.length === 0 && (
                <p className="collection-status collection-status--flush section-empty">
                    {hasFilter
                        ? 'Nothing matches that just yet. Try removing a filter above, or ask Bloom for something else.'
                        : 'Nothing to show here yet.'}
                </p>
            )}

            {hasFilter && total > 0 && (
                <div className="discover-reason">
                    {total} {total === 1 ? 'piece' : 'pieces'}
                </div>
            )}

            {/* A weight filter hides every piece with no weight recorded, which is
                most of them. Saying so is the difference between "there is nothing
                like that" and "we do not know the weight of these". */}
            {omitted > 0 && (
                <p className="collection-status collection-status--flush section-empty">
                    {omitted} {omitted === 1 ? 'piece has' : 'pieces have'} no weight recorded
                    and {omitted === 1 ? 'is' : 'are'} not shown.
                </p>
            )}

            {/* A reasoned row scrolls sideways; a filtered result wraps into the
                normal grid. The difference is intent — a default row is a
                suggestion to browse past, whereas a result set is the answer to
                something the user asked and should be visible in full. */}
            {rows.map((row, i) => (
                row.reason
                    ? <PagedRow
                        reason={row.reason}
                        cards={row.cards}
                        renderCard={renderCard(row.reason)}
                        dismissed={dismissed[rowKey(row.reason)] || []}
                        onUndo={e => restore(row.reason, [e])}
                        onRestoreAll={() => restore(row.reason, dismissed[rowKey(row.reason)] || [])}
                        key={row.reason} />
                    : <div key={i}>
                        <div className="collection-grid collection-grid--left">
                            {row.cards.map(renderCard(row.reason))}
                        </div>
                        {/* A filtered result has no reason and no arrows, but it can
                            still be dismissed from, so it needs the group too. */}
                        <DismissedGroup
                            entries={dismissed[rowKey(row.reason)] || []}
                            onUndo={e => restore(row.reason, [e])}
                            onRestoreAll={() => restore(row.reason, dismissed[rowKey(row.reason)] || [])} />
                      </div>
            ))}
        </>
    );
};

export default UserDiscover;
